import { Checkbox } from '@/components/Checkbox';
import { Icon, IconSize, IconVariant } from '@/components/Icon';
import { createPlural } from '@/helpers/createPlural';
import classnames from '@/utilities/classnames';
import React from 'react';

export interface WeeksNumberSelectionOption {
  value: number;
  label?: string;
}

export interface WeeksNumberSelectionProps {
  options: WeeksNumberSelectionOption[];
  selectedValue: number;
  onChange: (value: number) => void;
  suffixes?: string[];
  label?: string;
  hasWeeklySubscriptionOption?: boolean;
  selectedWeeklySubscription?: boolean;
  onWeeklySubscriptionChange?: () => void;
}

export class WeeksNumberSelection extends React.Component<WeeksNumberSelectionProps> {
  getSelectedOption = (): WeeksNumberSelectionOption => {
    return (
      this.props.options.find(
        (option) => option.value === this.props.selectedValue
      ) || this.props.options[0]
    );
  };

  getSelectedIndex = (): number => {
    const currentOption = this.getSelectedOption();
    return this.props.options.indexOf(currentOption);
  };

  prevIsDisabled = (): boolean => {
    return this.getSelectedIndex() === 0;
  };

  nextIsDisabled = (): boolean => {
    return this.getSelectedIndex() === this.props.options.length - 1;
  };

  constrainIndex = (index: number): number => {
    return Math.max(0, Math.min(this.props.options.length - 1, index));
  };

  selectOption = (diff: number): void => {
    if (this.props.selectedWeeklySubscription) {
      return;
    }
    const newIndex = this.constrainIndex(this.getSelectedIndex() + diff);
    const newOption = this.props.options[newIndex];
    this.props.onChange(newOption.value);
  };

  render(): JSX.Element {
    const selectedOption = this.getSelectedOption();
    const { selectedWeeklySubscription } = this.props;

    return (
      <div className="WeeksNumberSelection">
        <div
          className={classnames('WeeksNumberSelection__group', {
            'WeeksNumberSelection__group--disabled': selectedWeeklySubscription,
          })}
        >
          <span
            onClick={() => this.selectOption(-1)}
            className={classnames(
              'WeeksNumberSelection__button',
              'WeeksNumberSelection__button--prev',
              {
                'WeeksNumberSelection__button--disabled': this.prevIsDisabled(),
              }
            )}
            tabIndex={this.prevIsDisabled() ? -1 : 0}
            aria-label={`${
              this.props.label ? this.props.label : 'Wert'
            } verringern`}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                this.selectOption(-1);
              }
            }}
          >
            <Icon variant={IconVariant.Minus} />
          </span>
          <span className="WeeksNumberSelection__selected" aria-live="polite">
            {!!selectedOption.value &&
              (selectedOption.label || selectedOption.value) +
                createPlural(+selectedOption.value, this.props.suffixes)}
          </span>
          <span
            onClick={() => this.selectOption(1)}
            className={classnames(
              'WeeksNumberSelection__button',
              'WeeksNumberSelection__button--next',
              {
                'WeeksNumberSelection__button--disabled': this.nextIsDisabled(),
              }
            )}
            tabIndex={this.nextIsDisabled() ? -1 : 0}
            aria-label={`${
              this.props.label ? this.props.label : 'Wert'
            } erhöhen`}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                this.selectOption(1);
              }
            }}
          >
            <Icon variant={IconVariant.Plus} size={IconSize.ExtraSmall} />
          </span>
        </div>

        {this.props.hasWeeklySubscriptionOption &&
          this.props.onWeeklySubscriptionChange && (
            <Checkbox
              htmlId="WeeeklySubscription"
              value={true}
              checked={!!this.props.selectedWeeklySubscription}
              label={{ text: 'Dauerspiel' }}
              ariaLabel={`Dauerspiel - aktuell ${
                this.props.selectedWeeklySubscription ? '' : 'nicht'
              } ausgewählt`}
              onChange={this.props.onWeeklySubscriptionChange}
            />
          )}
      </div>
    );
  }
}
